import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaBars, FaTimes, FaHeart, FaBell, FaLanguage, FaCreditCard, FaShoppingBag, FaHome, FaFileAlt, FaStar, FaHeadset, FaBriefcase, FaChevronDown, FaCalendarAlt, FaClipboardList, FaChartBar, FaUsers, FaTasks } from 'react-icons/fa';
import { getApiUrl } from '../config/api';
import { getProfileImageUrl } from '../utils/imageHelper';
import { loadWishlistFavorites } from '../utils/wishlistStorage';
import { setGoogleTranslateLocale } from '../utils/languagePreference';
import { clearCompanyStaffSession, mergeUserWithStaffSession } from '../utils/companyStaffSession';
import './Navbar.css';

// Tier cards (Step 1): Essential, Advance, Expert – Universal Book & Stay Advisory app
const SUBSCRIPTION_TIERS = [
  {
    id: 'essential',
    name: '1 Month',
    price: '0',
    period: '/ 1 month',
    subtitle: 'Great for quick checks',
    originalPrice: 'Rs 100',
    description: 'Full access to legal solutions for short-term use.',
    features: [
      { icon: FaShoppingBag, text: 'Full access to legal solutions' },
      { icon: FaFileAlt, text: 'Public-level explanations' },
      { icon: FaHome, text: 'Search history & saved results' },
      { icon: FaHeadset, text: 'Email support' },
    ],
    buttonLabel: 'Claim Launch Offer',
    style: 'essential',
    tag: '',
  },
  {
    id: 'advance',
    name: '6 Months',
    price: '0',
    period: '/ 6 months',
    subtitle: 'Best value for steady use',
    originalPrice: 'Rs 450',
    description: 'Everything in Starter plus priority access and support.',
    features: [
      { icon: FaStar, text: 'Everything in Starter' },
      { icon: FaStar, text: 'Priority search speed' },
      { icon: FaFileAlt, text: 'Draft creation tools' },
      { icon: FaHeadset, text: 'WhatsApp support' },
    ],
    buttonLabel: 'Claim Launch Offer',
    style: 'advance',
    tag: 'Active Plan',
  },
  {
    id: 'expert',
    name: '1 Year',
    price: '0',
    period: '/ 1 year',
    subtitle: 'Built for long-term savings',
    originalPrice: 'Rs 800',
    description: 'Everything in Growth plus premium advisor access.',
    features: [
      { icon: FaShoppingBag, text: 'Everything in Growth' },
      { icon: FaBriefcase, text: 'Advanced legal summaries' },
      { icon: FaBriefcase, text: 'Premium advisor matching' },
      { icon: FaHeadset, text: 'Early access to new features' },
    ],
    buttonLabel: 'Claim Launch Offer',
    style: 'expert',
    tag: '',
  },
];

// Billing options (Step 2): 6 months default
const SUBSCRIPTION_PLANS = [
  { id: 'monthly', label: 'Monthly', months: 1, enabled: false },
  { id: '3months', label: '3 Months', months: 3, enabled: false },
  { id: '6months', label: '6 Months', months: 6, enabled: true },
  { id: 'yearly', label: 'Yearly', months: 12, enabled: false },
  { id: '3yearly', label: '3 Yearly', months: 36, enabled: false },
];
const MONTHLY_RATE = 0;
const HOST_DASHBOARD_ROLES = new Set(['host', 'company_admin', 'company_manager', 'company_staff']);
const COMPANY_WORKSPACE_ROLES = new Set(['company_admin', 'company_manager', 'company_staff']);
const COMPANY_DISPLAY_NAMES = {
  'abc-ltd': 'ABC Ltd',
  'pqr-ltd': 'PQR Ltd',
  'xyz-ltd': 'XYZ Ltd',
};
const COMPANY_EMAIL_HINTS = [
  { needle: '.abc', name: 'ABC Ltd' },
  { needle: '.pqr', name: 'PQR Ltd' },
  { needle: '.xyz', name: 'XYZ Ltd' },
];
const NOTIFICATION_FETCH_TIMEOUT_MS = 7000;

const MEETING_NAV_ITEMS = [
  {
    to: '/meetings/book',
    label: 'Book Room',
    icon: FaCalendarAlt,
    match: (pathname) => pathname === '/meetings/book',
  },
  {
    to: '/meetings/my-bookings',
    label: 'My Bookings',
    icon: FaClipboardList,
    match: (pathname) => pathname === '/meetings/my-bookings',
  },
  {
    to: '/meeting-book',
    label: 'Schedule',
    icon: FaChartBar,
    match: (pathname) => pathname === '/meeting-book' || pathname === '/meetings/schedule',
  },
  {
    to: '/meetings/create-team',
    label: 'Create Your Team',
    icon: FaUsers,
    match: (pathname) => pathname === '/meetings/create-team',
  },
  {
    to: '/meetings/status',
    label: 'Status',
    icon: FaTasks,
    match: (pathname) => pathname === '/meetings/status',
  },
];

// Confetti piece component
const ConfettiPiece = ({ style }) => <div className="sub-confetti-piece" style={style} aria-hidden="true" />;

// Full-screen Subscription Page component
const SubscriptionPage = ({ plans, monthlyRate, selectedPlan, onSelectPlan, onClose, onBack }) => {
  const [subscribed, setSubscribed] = useState(false);
  const [confetti, setConfetti] = useState([]);

  const activePlan = plans.find((p) => p.id === selectedPlan) || plans[2];
  const originalPrice = monthlyRate * activePlan.months;
  const finalPrice = 0;
  const discountPct = monthlyRate > 0 ? Math.round(((originalPrice - finalPrice) / originalPrice) * 100) : 100;

  const handleSubscribe = () => {
    // Generate confetti
    const pieces = Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 0.8}s`,
      backgroundColor: ['#ec4899', '#f472b6', '#fbcfe8', '#db2777', '#f9a8d4', '#fce7f3'][Math.floor(Math.random() * 6)],
      width: `${6 + Math.random() * 8}px`, 
      height: `${6 + Math.random() * 8}px`,
      borderRadius: Math.random() > 0.5 ? '50%' : '2px',
    }));
    setConfetti(pieces);
    setSubscribed(true);
    setTimeout(() => setConfetti([]), 3500);
  };

  return ReactDOM.createPortal(
    <div className="sub-fullpage" role="dialog" aria-label="Choose Your Subscription">
      {/* Confetti */}
      {confetti.map((p) => (
        <ConfettiPiece key={p.id} style={{ left: p.left, animationDelay: p.animationDelay, backgroundColor: p.backgroundColor, width: p.width, height: p.height, borderRadius: p.borderRadius }} />
      ))}

      {/* Launch Offer Banner */}
      <div className="sub-launch-banner">
        <span className="sub-launch-badge">LAUNCHING OFFER</span>
        <span className="sub-launch-text">100% OFF</span>
        <span className="sub-launch-subtext">Limited time access for everyone</span>
      </div>

      {/* Close button */}
      <button type="button" className="sub-close-btn" onClick={onClose} aria-label="Close">✕</button>

      {/* Back button */}
      {onBack && (
        <button type="button" className="sub-back-btn" onClick={onBack} aria-label="Back">
          ← Back
        </button>
      )}

      {/* Heading */}
      <div className="sub-heading-wrap">
        <h1 className="sub-heading">Choose Your Subscription</h1>
        <p className="sub-subheading">Unlock premium benefits with our most value-packed plan.</p>
      </div>

      {/* Main content */}
      <div className="sub-content">
        {/* Left: Plan list */}
        <div className="sub-plan-list">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const amt = plan.months * monthlyRate;
            const isBest = plan.id === '6months';
            return (
              <button
                key={plan.id}
                type="button"
                className={`sub-plan-row${isSelected ? ' sub-plan-row--active' : ''}${!plan.enabled ? ' sub-plan-row--disabled' : ''}`}
                onClick={() => plan.enabled && onSelectPlan(plan.id)}
                disabled={!plan.enabled}
              >
                <span className={`sub-plan-star${isSelected ? ' sub-plan-star--active' : ''}`}>★</span>
                <span className="sub-plan-info">
                  <span className="sub-plan-name">
                    {plan.label}
                    {isBest && <span className="sub-plan-badge">BEST OFFER</span>}
                  </span>
                  <span className="sub-plan-months">{plan.months} Month{plan.months > 1 ? 's' : ''}</span>
                </span>
                <span className="sub-plan-price">₹{amt.toLocaleString('en-IN')}</span>
              </button>
            );
          })}
        </div>

        {/* Right: Pricing panel */}
        <div className="sub-pricing-panel">
          <div className="sub-pricing-header">
            <span className="sub-pricing-plan-name">{activePlan.label}</span>
            {discountPct > 0 && <span className="sub-pricing-badge">{discountPct}% OFF</span>}
          </div>
          <div className="sub-pricing-rows">
            <div className="sub-pricing-row">
              <span>Monthly Price</span>
              <span>₹{monthlyRate.toLocaleString('en-IN')}</span>
            </div>
            <div className="sub-pricing-row">
              <span>Original Price</span>
              <span>₹{originalPrice.toLocaleString('en-IN')}</span>
            </div>
          </div>
          <div className="sub-final-box">
            <span className="sub-final-label">FINAL PRICE</span>
            <span className="sub-final-price">₹{finalPrice}</span>
          </div>

          {subscribed ? (
            <button type="button" className="sub-subscribed-btn" disabled>✓ Subscribed</button>
          ) : (
            <button type="button" className="sub-subscribe-btn" onClick={handleSubscribe}>
              Subscribe Now
            </button>
          )}
          <button type="button" className="sub-cancel-btn" onClick={onClose}>Cancel Subscription</button>
        </div>
      </div>

      {/* Success popup */}
      {subscribed && (
        <div className="sub-success-overlay" role="alertdialog" aria-label="Subscription Successful">
          <div className="sub-success-card">
            <div className="sub-success-icon">✓</div>
            <h3 className="sub-success-title">Subscription Successful</h3>
            <p className="sub-success-msg">Your {activePlan.label} premium plan is now active.</p>
            <button type="button" className="sub-success-ok" onClick={onClose}>OK</button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

const Navbar = ({ isScrolled, onSignInClick, showLoginModal = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [meetingMenuOpen, setMeetingMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountModalStep, setAccountModalStep] = useState(1);
  const [selectedTier, setSelectedTier] = useState('essential');
  const [selectedPlan, setSelectedPlan] = useState('6months');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const notificationFetchErrorLoggedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Languages for Google Translate (must match index.html includedLanguages)
  const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'kn', label: 'ಕನ್ನಡ' },
    { code: 'ta', label: 'தமிழ்' },
    { code: 'te', label: 'తెలుగు' },
    { code: 'bn', label: 'বাংলা' },
    { code: 'mr', label: 'मराठी' },
    { code: 'ml', label: 'മലയാളം' },
    { code: 'gu', label: 'ગુજરાતી' },
    { code: 'pa', label: 'ਪੰਜਾਬੀ' },
    { code: 'ur', label: 'اردو' },
    { code: 'or', label: 'ଓଡ଼ିଆ' },
    { code: 'sa', label: 'संस्कृतम्' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'ar', label: 'العربية' }
  ];

  const handleLanguageSelect = (code) => {
    setGoogleTranslateLocale(code === 'en' ? 'en' : code);
    document.documentElement.lang = code || 'en';
    setLangMenuOpen(false);
    window.location.reload();
  };

  // Open account modal: show tier cards (Step 1), Essential selected by default
  const openAccountModal = () => {
    setAccountModalStep(1);
    setSelectedTier('essential');
    setSelectedPlan('6months');
    setAccountModalOpen(true);
  };

  // Check if current route is a meeting page
  const isMeetingPage = location.pathname.startsWith('/meeting');

  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          const parsedUser = mergeUserWithStaffSession(JSON.parse(storedUser));
          setCurrentUser(parsedUser);

          // ✅ SECURITY: Try to derive wishlist count from user-specific localStorage favorites
          try {
            // Get user-specific favorites key
            const getFavoritesKey = () => {
              try {
                return `favorites_${parsedUser._id || parsedUser.id}`;
              } catch (error) {
                return 'favorites';
              }
            };

            const favoritesKey = getFavoritesKey();
            const favorites = loadWishlistFavorites(favoritesKey);
            setWishlistCount(favorites.length);
          } catch (error) {
            setWishlistCount(0);
          }

          // Migrate Google profile picture to local (avoids 429 from lh3.googleusercontent.com)
          const pic = parsedUser?.profilePicture;
          if (pic && typeof pic === 'string' && pic.includes('googleusercontent.com')) {
            const uid = parsedUser._id || parsedUser.id;
            const key = `migrateAttempted_${uid}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              fetch(getApiUrl('/api/auth/migrate-google-profile-picture'), {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${storedToken}` }
              })
                .then((r) => r.ok && r.json())
                .then((data) => {
                  if (data?.success && data?.user?.profilePicture) {
                    const updated = { ...parsedUser, profilePicture: data.user.profilePicture };
                    localStorage.setItem('user', JSON.stringify(updated));
                    setCurrentUser(updated);
                    window.dispatchEvent(new CustomEvent('userProfileUpdated'));
                  }
                })
                .catch(() => {});
            }
          }

          // Load notification count from API (guest only – host notifications stay on host dashboard)
          fetchNotificationCount(parsedUser, storedToken);
        } else {
          setCurrentUser(null);
          setWishlistCount(0);
          setNotificationCount(0);
        }
      } catch {
        setCurrentUser(null);
        setWishlistCount(0);
        setNotificationCount(0);
      }
    };

    loadUser();

    // Poll for notification updates every 10 seconds
    const notificationInterval = setInterval(() => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      if (storedUser && storedToken) {
        const parsedUser = JSON.parse(storedUser);
        fetchNotificationCount(parsedUser, storedToken);
      }
    }, 20000);

    const handleStorage = (event) => {
      if (event.key === 'user' || event.key === 'token') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('storage', loadUser);
    
    // Listen for notification badge update (when user marks as read on NotificationsPage)
    const handleBadgeUpdate = (e) => {
      if (e.detail?.unreadCount !== undefined) {
        setNotificationCount(e.detail.unreadCount);
      } else {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          fetchNotificationCount(JSON.parse(storedUser), storedToken);
        }
      }
    };
    window.addEventListener('notificationBadgeUpdate', handleBadgeUpdate);

    // Listen for profile updates (when user saves changes on Account page)
    const handleProfileUpdate = () => loadUser();
    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    
    return () => {
      clearInterval(notificationInterval);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('storage', loadUser);
      window.removeEventListener('notificationBadgeUpdate', handleBadgeUpdate);
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    setMeetingMenuOpen(false);
  }, [location.pathname]);

  // Fetch notification count from API – always guest context so guest pages never show host notifications
  const fetchNotificationCount = async (user, token) => {
    if (!user || !token) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), NOTIFICATION_FETCH_TIMEOUT_MS);
    try {
      const url = `${getApiUrl('/api/notifications/my-notifications')}?userType=guest&unreadOnly=true`;
      const response = await fetch(
        url,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        }
      );

      if (!response.ok) {
        throw new Error(`notification_count_http_${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        const count = result.unreadCount || 0;
        setNotificationCount(count);
        notificationFetchErrorLoggedRef.current = false;
        
        // Store in localStorage (bell links to guest notifications page)
        const userId = user._id || user.id;
        const notificationCountKey = `notificationCount_guest_${userId}`;
        localStorage.setItem(notificationCountKey, count.toString());
      }
    } catch (error) {
      if (!notificationFetchErrorLoggedRef.current && process.env.NODE_ENV === 'development') {
        console.warn('Notification count fetch skipped:', error?.message || error);
        notificationFetchErrorLoggedRef.current = true;
      }
      // Fallback to localStorage (bell shows guest notification count)
      try {
        const userId = user._id || user.id;
        const notificationCountKey = `notificationCount_guest_${userId}`;
        const storedCount = localStorage.getItem(notificationCountKey);
        if (storedCount) {
          setNotificationCount(parseInt(storedCount) || 0);
        }
      } catch (err) {
        setNotificationCount(0);
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const isAuthenticated = !!currentUser;
  const isHost = HOST_DASHBOARD_ROLES.has(String(currentUser?.role || '')) || Boolean(currentUser?.company?._id || currentUser?.company);
  const isCompanyWorkspaceUser =
    COMPANY_WORKSPACE_ROLES.has(String(currentUser?.role || '')) ||
    Boolean(currentUser?.company?._id || currentUser?.company);
  const activeMeetingNavItem = MEETING_NAV_ITEMS.find((item) => item.match(location.pathname));
  const activeMeetingMenuLabel = activeMeetingNavItem?.label || 'Private Workspace Menu';
  const getCompanyDisplayName = (user) => {
    if (!user) return '';
    const company = user.company;
    if (company && typeof company === 'object') {
      const companyName = company.profile?.displayName || company.name || COMPANY_DISPLAY_NAMES[company.slug];
      if (companyName) return companyName;
    }

    const companySlug = typeof company === 'string' ? company : user.companySlug;
    if (companySlug && COMPANY_DISPLAY_NAMES[String(companySlug).toLowerCase()]) {
      return COMPANY_DISPLAY_NAMES[String(companySlug).toLowerCase()];
    }

    const emailLocalPart = String(user.email || '').toLowerCase().split('@')[0] || '';
    const matchedHint = COMPANY_EMAIL_HINTS.find((hint) => emailLocalPart.includes(hint.needle));
    return matchedHint?.name || '';
  };

  const getStaffDisplayName = (user) => {
    const name = String(user?.staffProfile?.name || '').trim();
    return name || '';
  };

  const getAccountDisplayName = () => {
    if (!currentUser) return 'Profile';
    const staffName = getStaffDisplayName(currentUser);
    const companyName = getCompanyDisplayName(currentUser);
    if (staffName && companyName) return `${staffName} • ${companyName}`;
    if (staffName) return staffName;
    if (companyName) return companyName;
    return String(currentUser.name || currentUser.email || 'Profile').trim();
  };

  const getUserAvatarLabel = () => {
    if (!currentUser) return 'Profile';
    const companyName = getCompanyDisplayName(currentUser);
    return companyName || 'Profile';
  };

  const getUserInitials = () => {
    if (!currentUser) return 'U';
    const base =
      getStaffDisplayName(currentUser) ||
      getCompanyDisplayName(currentUser) ||
      currentUser.name ||
      currentUser.email ||
      '';
    const name = String(base).trim();
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!currentUser) return 'Profile';
    const base = String(
      getStaffDisplayName(currentUser) ||
      getCompanyDisplayName(currentUser) ||
      currentUser.name ||
      currentUser.email ||
      ''
    ).trim();
    if (!base) return 'Profile';
    return base;
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
  };

  const confirmLogout = () => {
    // ✅ SECURITY: Clear all user-related data on logout
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        const userId = userObj._id || userObj.id;
        localStorage.removeItem(`officeSpaceAnswered_${userId}`);
        localStorage.removeItem(`notificationCount_guest_${userId}`);
        localStorage.removeItem(`notificationCount_host_${userId}`);
        // Clear user-specific popup flag
        localStorage.removeItem(`meetingHomePopupShown_${userId}`);
      }
    } catch (error) {
      console.error('Error clearing user-specific data:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearCompanyStaffSession();
    // Clear old non-user-specific popup flag (for backward compatibility)
    localStorage.removeItem('meetingHomePopupShown');
    
    // Clear all localStorage keys that might contain user data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('officeSpaceAnswered_') || key.startsWith('user_') || key.startsWith('booking_') || key.startsWith('notificationCount_') || key.startsWith('lastSearch_') || key.startsWith('recentSearches_') || key.startsWith('meetingHomePopupShown_')) {
        localStorage.removeItem(key);
      }
    });
    setCurrentUser(null);
    setShowLogoutModal(false);
    navigate('/');
    window.location.reload();
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <motion.nav 
      className={`navbar ${isScrolled ? 'scrolled' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="navbar-container">

      <Link to="/" className="navbar-logo" aria-label="UBSAAPP home">
        <img
          src="/ubsaapp-topnav-logo.png"
          alt="UBSAAPP icon"
          className="navbar-logo-icon"
        />
      </Link>

        {/* Center - Meeting room links (middle of navbar) */}
        <div className={`navbar-menu navbar-menu-center ${isMenuOpen ? 'active' : ''}`}>
          {isAuthenticated && isMeetingPage && (
            <div className="meeting-dropdown-menu">
              {meetingMenuOpen && (
                <button
                  type="button"
                  className="meeting-dropdown-overlay"
                  aria-label="Close meeting menu"
                  onClick={() => setMeetingMenuOpen(false)}
                />
              )}
              <button
                type="button"
                className={`meeting-dropdown-trigger${meetingMenuOpen ? ' open' : ''}`}
                aria-haspopup="true"
                aria-expanded={meetingMenuOpen}
                onClick={() => setMeetingMenuOpen((open) => !open)}
              >
                <span className="meeting-dropdown-trigger-copy">
                  <span className="meeting-dropdown-trigger-kicker">Private Workspace Menu</span>
                  <span className="meeting-dropdown-trigger-label">{activeMeetingMenuLabel}</span>
                </span>
                <FaChevronDown className="meeting-dropdown-trigger-caret" aria-hidden="true" />
              </button>

              {meetingMenuOpen && (
                <div className="meeting-dropdown-panel" role="menu" aria-label="Meeting navigation">
                  {MEETING_NAV_ITEMS.map(({ to, label, icon: Icon, match }) => (
                    <NavLink
                      key={to}
                      to={to}
                      role="menuitem"
                      onClick={() => setMeetingMenuOpen(false)}
                      className={({ isActive }) =>
                        `meeting-dropdown-link${isActive || match(location.pathname) ? ' active' : ''}`
                      }
                    >
                      <span className="meeting-dropdown-link-icon-wrap">
                        <Icon className="meeting-dropdown-link-icon" aria-hidden />
                      </span>
                      <span className="meeting-dropdown-link-copy">
                        <span className="meeting-dropdown-link-label">{label}</span>
                        <span className="meeting-dropdown-link-meta">Open section</span>
                      </span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="navbar-actions">
          {/* Language / Translate icon - triggers Google Translate */}
          <div className="navbar-lang-wrap">
            <button
              type="button"
              className="icon-button navbar-lang-btn"
              aria-label="Choose language"
              aria-haspopup="true"
              aria-expanded={langMenuOpen}
              onClick={() => setLangMenuOpen((o) => !o)}
            >
              <FaLanguage />
            </button>
            {langMenuOpen && (
              <>
                <div className="navbar-lang-backdrop" onClick={() => setLangMenuOpen(false)} aria-hidden="true" />
                <div className="navbar-lang-dropdown" role="menu">
                  <div className="navbar-lang-dropdown-title">Language / ಭಾಷೆ</div>
                  {LANGUAGES.map(({ code, label }) => (
                    <button
                      key={code}
                      type="button"
                      className="navbar-lang-option"
                      onClick={() => handleLanguageSelect(code)}
                      role="menuitem"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Subscription button - next to language button */}
          <button
            type="button"
            className="navbar-subscription-btn desktop-only"
            onClick={openAccountModal}
            aria-label="Subscription"
          >
            <FaCreditCard className="subscription-icon" />
            <span className="subscription-text">Subscription</span>
          </button>

          {!isAuthenticated ? (
            <>
              {showLoginModal && onSignInClick ? (
                <button
                  onClick={onSignInClick}
                  className="navbar-link sign-in-btn"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <FaUser className="user-icon" />
                  <span>Sign in</span>
                </button>
              ) : (
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `navbar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <FaUser className="user-icon" />
                  <span>Sign in</span>
                </NavLink>
              )}

              {/* Become a Host CTA for guests (not logged-in) */}
              <button
                className="btn-host"
                onClick={(e) => {
                  e.preventDefault();
                  // Store redirect path to /host
                  localStorage.setItem('authRedirectPath', '/host');
                  // If onSignInClick is provided (from MeetingHome), use it
                  if (onSignInClick) {
                    onSignInClick();
                  } else {
                    // Otherwise, navigate to /host page (which will show login prompt)
                    navigate('/host');
                  }
                }}
              >
                Become a Host
              </button>
            </>
          ) : (
            <>
              {/* Wishlist icon (Saved stays) */}
              <Link to="/wishlist" className="icon-button" aria-label="Saved stays">
                <FaHeart />
                {wishlistCount > 0 && (
                  <span className="icon-badge">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>

              {/* Notifications icon */}
              <button
                type="button"
                className="icon-button"
                aria-label="Notifications"
                onClick={() => navigate('/notifications')}
              >
                <FaBell />
                {notificationCount > 0 && (
                  <span className="icon-badge">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* Switch to Host View - show for all logged-in users */}
              {isAuthenticated && (
                <button 
                  className="btn-host desktop-only"
                  onClick={() => {
                    // Navigate to host page - user is already logged in
                    navigate('/host');
                  }}
                >
                  {isHost ? 'list properties' : 'Become a Host'}
                </button>
              )}

              {/* User avatar & dropdown */}
              <div className="navbar-user">
                <button
                  type="button"
                  className={`user-avatar-button ${isUserMenuOpen ? 'open' : ''}`}
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  aria-haspopup="true"
                  aria-expanded={isUserMenuOpen}
                >
                  <div className="user-avatar-circle">
                    {getProfileImageUrl(currentUser?.profilePicture) ? (
                      <img 
                        src={getProfileImageUrl(currentUser.profilePicture)} 
                        alt={getUserDisplayName()} 
                        className="user-avatar-image"
                      />
                    ) : (
                      <span>{getUserInitials()}</span>
                    )}
                  </div>
                  <div className="user-avatar-meta desktop-only">
                    <span className="user-avatar-label">{getUserAvatarLabel()}</span>
                    <span className="user-avatar-name">{getUserDisplayName()}</span>
                  </div>
                  <FaChevronDown className="user-avatar-caret desktop-only" aria-hidden="true" />
                </button>
                {isUserMenuOpen && (
                  <div className="user-menu-dropdown" role="menu">
                    <div className="user-menu-header">
                      <span className="user-menu-name">
                        {getAccountDisplayName()}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="user-menu-item user-menu-item-mobile-only"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        openAccountModal();
                      }}
                    >
                      <FaCreditCard className="user-menu-item-icon" />
                      Subscription 
                    </button>
                    <Link
                      to="/trips"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      My Bookings
                    </Link>
                    {isMeetingPage ? (
                      <Link
                        to="/"
                        className="user-menu-item"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Book Your Stay
                      </Link>
                    ) : (
                      <Link
                        to="/meeting-home"
                        className="user-menu-item"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Book Your Meeting
                      </Link>
                    )}
                    <Link
                      to="/wishlist"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Saved Stays
                    </Link>
                    {isCompanyWorkspaceUser && (
                      <Link
                        to="/workspace"
                        className="user-menu-item"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Workspace
                      </Link>
                    )}
                    <Link
                      to="/host"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      List Property
                    </Link>
                    {isMeetingPage && (
                      <Link
                        to="/meetings/add-your-space"
                        className="user-menu-item"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Add Your Space
                      </Link>
                    )}
                    <Link
                      to="/account"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Profile Settings
                    </Link>
                    <Link
                      to="/help"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Help &amp; Support
                    </Link>
                    <Link
                      to="/feedback"
                      className="user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Feedback
                    </Link>
                    {!isHost && (
                      <Link
                        to="/host"
                        className="user-menu-item"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Switch to Host View
                      </Link>
                    )}
                    <button
                      type="button"
                      className="user-menu-item logout"
                      onClick={handleLogout}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          <button 
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

      </div>

      {/* Step 1: Account & Subscription - full screen tier page */}
      {accountModalOpen && accountModalStep === 1 && ReactDOM.createPortal(
        <div className="sub-fullpage" role="dialog" aria-label="Account & Subscription">
          <button type="button" className="sub-close-btn" onClick={() => setAccountModalOpen(false)} aria-label="Close">✕</button>
          <div className="sub-heading-wrap">
            <h1 className="sub-heading">Account &amp; Subscription</h1>
            <p className="sub-subheading">Choose your membership and unlock member-only perks.</p>
          </div>
          <div className="acct-hero-banner">
            <div>
              <span className="acct-hero-label">Transparent Pricing</span>
              <h2 className="acct-hero-title">Pick a plan that grows with your travel needs</h2>
              <p className="acct-hero-description">Simple monthly options, smarter long-term savings. Choose a plan and get instant access to premium booking solutions, tailored support, and launch offers.</p>
              <div className="acct-hero-tags">
                {['Instant access', 'Secure payments', 'Cancel anytime'].map((item) => (
                  <span key={item} className="acct-hero-tag">{item}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="acct-tier-grid">
            {SUBSCRIPTION_TIERS.map((tier) => {
              const isSelected = selectedTier === tier.id;
              const isDisabled = tier.id !== 'advance'; // Only 6 Months (advance) is enabled
              return (
                <div
                  key={tier.id}
                  className={`acct-tier-card acct-tier-${tier.style}${isSelected ? ' acct-tier-selected' : ''}${isDisabled ? ' acct-tier-disabled' : ''}`}
                  onClick={isDisabled ? undefined : () => setSelectedTier(tier.id)}
                  role="button" 
                  tabIndex={isDisabled ? -1 : 0}
                  onKeyDown={isDisabled ? undefined : (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTier(tier.id); } }}
                  aria-pressed={isDisabled ? undefined : isSelected}
                  aria-disabled={isDisabled ? "true" : undefined}
                >
                  <div className="acct-tier-card-top">
                    <div>
                      <span className="acct-tier-name">{tier.name}</span>
                      <span className="acct-tier-subtitle">{tier.subtitle}</span>
                    </div>
                    {tier.tag && <span className={`acct-tier-tag acct-tier-tag-${tier.style}`}>{tier.tag}</span>}
                  </div>
                  <div className="acct-tier-price-block">
                    <div>
                      <span className="acct-tier-main-price">Rs {tier.price}</span>
                      <span className="acct-tier-period">{tier.period}</span>
                    </div>
                    <span className="acct-tier-original-price">{tier.originalPrice}</span>
                  </div>
                  <p className="acct-tier-desc">{tier.description}</p>
                  <ul className="acct-tier-features">
                    {tier.features.map((f, i) => {
                      const FIcon = f.icon;
                      return (
                        <li key={i}>
                          {FIcon && <FIcon style={{ flexShrink: 0, width: '0.8rem', height: '0.8rem', opacity: 0.7 }} />}
                          <span>{f.text}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <button
                    type="button"
                    className={`acct-tier-btn acct-tier-btn-${tier.style}`}
                    disabled={isDisabled}
                    aria-disabled={isDisabled ? "true" : undefined}
                    onClick={isDisabled ? undefined : (e) => {
                      e.stopPropagation();
                      setAccountModalStep(2); // Navigate to Choose Your Subscription page
                    }}
                  >
                    {tier.buttonLabel}
                  </button>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* Step 2: Choose Your Subscription full page */}
      {accountModalOpen && accountModalStep === 2 && (
        <SubscriptionPage
          plans={SUBSCRIPTION_PLANS}
          monthlyRate={MONTHLY_RATE}
          selectedPlan={selectedPlan}
          onSelectPlan={setSelectedPlan}
          onClose={() => setAccountModalOpen(false)}
          onBack={() => setAccountModalStep(1)}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className="logout-modal-overlay" onClick={cancelLogout}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="logout-modal-actions">
              <button type="button" className="logout-btn-no" onClick={cancelLogout}>
                No
              </button>
              <button type="button" className="logout-btn-yes" onClick={confirmLogout}>
                Yes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.nav>
  );
};

export default Navbar;
